import ZoomVideo from "@zoom/videosdk";
import axios from "axios";
import { put, takeLatest } from "redux-saga/effects";

import { IInitialState } from "@/types/initialState.types";
import { IUserIdentityArgs, IZoomGlobalArgs } from "@/types/zoomClient.types";
import { showToast } from "@/actions/appStatus.action";
import {
  CREATE_ZOOM_CLIENT,
  REMOVE_ZOOM_CLIENT,
  SAVE_ZOOM_STREAM,
  UPDATE_ZOOM_AUDIO_STARTED,
  UPDATE_ZOOM_CONNECTION_STATUS,
  UPDATE_ZOOM_VIDEO_STARTED,
} from "@/constants/ActionTypes";
import { joinClient, leaveUserFromCall } from "@/utils/zoom.utils";
import { EToastType } from "@/components/common/AlertToast/AlertToast.types";
import { EUserRole } from "@/types/auth.types";
import {
  errorLogger,
  EErrorLevel,
  EErrorSource,
} from "@/utils/ubaTracking.utils";
import { call, select } from "./utils.saga";

function* createZoomClient({ payload }: IUserIdentityArgs) {
  try {
    const { name, role } = payload;
    const { interviewSlug, serverTimeOffsetTime } = yield* select(
      (state: IInitialState) => ({
        interviewSlug: state.interview?.slug,
        serverTimeOffsetTime: state.serverTime.offsetTime,
      }),
    );
    const client = ZoomVideo.createClient();

    if (interviewSlug && client) {
      const joinClientResp = yield* call(
        joinClient,
        client,
        name,
        role,
        interviewSlug,
        serverTimeOffsetTime,
      );

      if (joinClientResp) {
        yield put({
          type: `${CREATE_ZOOM_CLIENT}_SUCCESS`,
          payload: {
            client,
          },
        });
        const stream = client.getMediaStream();
        yield put({
          type: `${SAVE_ZOOM_STREAM}_SUCCESS`,
          payload: {
            stream,
          },
        });
      }
    }
  } catch (error: unknown) {
    errorLogger(error as Error, EErrorLevel.HIGH, EErrorSource.ZOOM);
    yield put({
      type: `${CREATE_ZOOM_CLIENT}_SUCCESS`,
      payload: {
        client: null,
        stream: null,
      },
    });
    yield put(
      showToast({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        message: axios.isAxiosError(error) ? error.response?.data.message : "",
        type: EToastType.ERROR,
      }),
    );
  }
}

function* removeZoomClient() {
  try {
    const { role, client } = yield* select((state: IInitialState) => ({
      role: state.auth.userIdentityData?.role,
      client: state.zoomClient.client,
      stream: state.zoomClient.stream,
    }));
    const hasZoomHostPermission = role === EUserRole.INTERVIEWER;
    yield call(leaveUserFromCall, client, hasZoomHostPermission);
    ZoomVideo.destroyClient();
    yield put({
      type: `${REMOVE_ZOOM_CLIENT}_SUCCESS`,
    });
  } catch (error: unknown) {
    errorLogger(error as Error, EErrorLevel.HIGH, EErrorSource.ZOOM);
  }
}

function* updateZoomVideoStarted({ payload }: IZoomGlobalArgs) {
  try {
    yield put({
      type: `${UPDATE_ZOOM_VIDEO_STARTED}_SUCCESS`,
      payload: {
        zoomVideoStarted: payload.zoomVideoStarted,
      },
    });
  } catch (error) {
    errorLogger(error as Error, EErrorLevel.HIGH, EErrorSource.ZOOM);
    yield put({
      type: `${UPDATE_ZOOM_VIDEO_STARTED}_SUCCESS`,
      payload: {
        zoomVideoStarted: false,
      },
    });
  }
}

function* updateZoomAudioStarted({ payload }: IZoomGlobalArgs) {
  try {
    yield put({
      type: `${UPDATE_ZOOM_AUDIO_STARTED}_SUCCESS`,
      payload: {
        zoomAudioStarted: payload.zoomAudioStarted,
      },
    });
  } catch (error) {
    errorLogger(error as Error, EErrorLevel.HIGH, EErrorSource.ZOOM);
    yield put({
      type: `${UPDATE_ZOOM_AUDIO_STARTED}_SUCCESS`,
      payload: {
        zoomAudioStarted: false,
      },
    });
  }
}

function* updateZoomConnectionStatus({ payload }: IZoomGlobalArgs) {
  try {
    yield put({
      type: `${UPDATE_ZOOM_CONNECTION_STATUS}_SUCCESS`,
      payload: {
        zoomConnectionStatus: payload.zoomConnectionStatus,
      },
    });
  } catch (error) {
    errorLogger(error as Error, EErrorLevel.HIGH, EErrorSource.ZOOM);
    yield put({
      type: `${UPDATE_ZOOM_CONNECTION_STATUS}_SUCCESS`,
      payload: {
        zoomConnectionStatus: null,
      },
    });
  }
}

export default function* loadZoomClientSaga() {
  yield takeLatest(CREATE_ZOOM_CLIENT, createZoomClient);
  yield takeLatest(UPDATE_ZOOM_VIDEO_STARTED, updateZoomVideoStarted);
  yield takeLatest(UPDATE_ZOOM_AUDIO_STARTED, updateZoomAudioStarted);
  yield takeLatest(REMOVE_ZOOM_CLIENT, removeZoomClient);
  yield takeLatest(UPDATE_ZOOM_CONNECTION_STATUS, updateZoomConnectionStatus);
}
